<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import { ProductCode } from '~/types'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Comprehensive Cover | Auto Insurance for businesses',
		description: 'Comprehensive auto insurance for smart businesses',
		tags: ['Auto insurance, businesses'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/comprehensive-cover`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()

	const benefitHighlights = [
		{
			title: 'Get covered',
			description:
				'Getting auto Insurance shouldn’t waste time. You receive your insurance certificate instantly.',
			image: '/v1727082981/ivc_l9sudc.webp',
			open: true
		},
		{
			title: 'Fast claims',
			description:
				'Claims settlement is digital, fast, seamless, and transparent powered by our A.I claims solution.',
			image: '/v1727082984/qcp_oxiunb.webp'
		},
		{
			title: 'Flexible',
			description:
				'You’ve already spent a lot on your vehicles. You shouldn’t have to pay so much to protect them.',
			image: '/v1727082981/auto-bfo_gezk97.webp'
		},
		{
			title: 'Valid insurance policies',
			description:
				'Our auto insurance policies are licensed and genuine. Once the purchase is completed, the policy is automatically uploaded on the NIID database, hence assuring verification of certificate by law enforcement officials.',
			image: '/v1727082982/inu_xwtqqi.webp'
		},
		{
			title: 'Bulk processing',
			description:
				"This feature enables you to upload your entire fleet's information in one go, saving you time and effort.",
			image: '/v1727082967/highlight-4_rr0xhn.webp'
		},
		{
			title: 'Delegate like a genius',
			description: 'Assign roles and permissions to trusted teammates. Share the control, not the stress.',
			image: '/v1727082977/oan_gqspqy.webp'
		},
		{
			title: 'Analytics and insights',
			description:
				'View transaction history from your account dashboard, including premium payments, employee utilisation, and renewals.',
			image: '/v1727082982/cph_e6lxub.webp'
		},
		{
			title: 'Employee Mobile App',
			description:
				"When trouble hits the road, your employees won't be stuck. They'll have the app, the cover, and the control accessible to them.",
			image: '/v1761841931/mobile-app-comms_opxlgb.webp'
		}
	]

	const faqList = ref<FAQContext[]>([
		{
			question: 'What does comprehensive auto insurance cover?',
			answer: 'Comprehensive auto insurance covers a wide range of incidents, including theft, fire, vandalism, and damage from natural disasters like floods or storms. It also covers damages that are not related to collisions and provides protection for both you and a third party involved in an accident.',
			open: true
		},
		{
			question: 'Will I get a certificate when I complete my auto insurance registration?',
			answer: 'Absolutely. Once you purchase an auto plan and provide your vehicle details, your certificate will be generated instantly. You can hit the road with confidence, knowing you are fully covered and compliant with legal requirements.',
			open: false
		},
		{
			question: 'When will my policy become active after I activate it?',
			answer: 'Once you activate your auto insurance plan, your policy number and certificate will be generated immediately. You can then enjoy your ride on the road, fully covered and with no bother',
			open: false
		},
		{
			question: 'How can I make claim?',
			answer: 'Our claim process is AI-powered, especially during inspections, making it quick and easy to get your claim approved without a lot of back and forth.',
			open: false
		},
		{
			question: 'What is the cost of Comprehensive Auto?',
			answer: "The cost is 5% of the vehicle's value per annum, or 0.5% of the vehicle's value if you choose to pay monthly",
			open: false
		},
		{
			question: 'What does Comprehensive Auto cover?',
			answer: 'Comprehensive auto insurance covers a wide range of incidents, including theft, fire, vandalism, and damage from natural disasters like floods or storms. It also covers damages that are not related to collisions and provides protection for both you and a third party involved in an accident.',
			open: false
		},
		{
			question: 'What is excess buyback in Comprehensive Auto?',
			answer: `Typically, insurance policies require you to pay an "excess" (a fixed amount) before the insurer covers the rest of the claim. However, with Excess Buyback, you won't need to pay any excess—with comprehensive auto, we will cover the full claim amount without deductions`,
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Comprehensive Cover',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727102474/comprehensive_kod7jn.webp',
						url: seo.url,
						price: '0'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Comprehensive Cover', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'auto-wishlist',
				height: 80,
				width: 60
			},
			title: 'Vehicle details',
			description: 'Fill in the required details to <br /> register your vehicles.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'auto-card',
				height: 140,
				width: 140
			},
			title: 'Make payment',
			description:
				'Once your vehicle details are entered, <br />  proceed to make the payment for insurance <br /> coverage and complete your purchase.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'travel-umbrella',
				height: 80,
				width: 80
			},
			title: 'Get certificate instantly',
			description:
				'After completing the payment and <br /> activation process, receive your insurance <br /> certificate via email.'
		}
	])
	const btnClass: H1Config = {
		tag: { base: 'bg-main text-white sm:hidden mb-5' },
		wrapperClass: {
			alignItems: '',
			overflow: '',
			maxWidth: 'max-w-[673px] w-full'
		},
		hClass: {
			wrapper: {
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]',
				alignItems: ''
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[586px]',
			text: 'xl:text-xl text-base md:text-lg',
			base: 'text sm:mt-8 mt-5',
			leading: 'md:leading-[32px]'
		},
		btn: {
			base: 'sm:mt-20',
			primary: {
				btn: {
					base: 'bg-white  text-base text-auto-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-auto-500'
				},
				text: {
					size: 'text-sm sm:text-base'
				}
			},
			secondary: {
				base: 'default-btn  max-w-[405px] text-center justify-center w-full gap-x-3',
				text: 'text-sm sm:text-base'
			}
		}
	}
	const guideList: GuideListContext = [
		{
			tab: 'claim_process',
			label: 'Simple. Fast. Transparent.',
			description: "That's our vehicle insurance claims process in three words. Here's how it works",
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text',
						height: 16,
						width: 16
					},
					title: 'Report Claim',
					description:
						'File your claim instantly from your dashboard. No need for endless calls or paperwork.',
					imageSrc: '/v1727082821/auto-claim_hsfvml.gif',
					show: true
				},
				{
					title: 'Claims assessment',
					description: 'Your claim is quickly reviewed to ensure a smooth process.',
					iconTag: {
						dir: 'random',
						icon: 'clock',
						height: 16,
						width: 16
					},
					imageSrc: '/v1727082834/auto-claim-2_rlizty.gif'
				},
				{
					title: 'Instant payout',
					description:
						"Got the green light? Enjoy speedy payouts for approved claims – because getting back on the road shouldn't be a wait.",
					iconTag: {
						dir: 'random',
						icon: 'receipt',
						height: 16,
						width: 16
					},
					imageSrc: '/v1727082957/gadget-claim-3_rals23.webp'
				}
			]
		}
	]
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Accidental <br /> damage',
			description:
				'This protects your business against loss or <br /> damage to your vehicle reulting from road accidents.',
			icon: {
				icon: 'car-breakdown',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Fire and <br /> theft',
			description: 'Get speedy compensation in the case of <br /> vehicle theft or fire damage.',
			icon: {
				icon: 'car-accident',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Third party vehicle <br /> damage',
			description:
				"Rest assured knowing that you're protected against any liability & potential expenses resulting from damage to third party properties.",
			icon: {
				icon: 'auto-users-add',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Unlimited bodily injury <br /> coverage',
			description: 'Enjoy unlimited coverage for bodily injury to your staff and others.',
			icon: {
				icon: 'auto-injury',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Complimentary <br /> tracking device',
			description:
				'Vehicles valued at ₦6,000,000 and above qualify for a complimentary tracking device, enhancing security and enabling efficient tracking',
			icon: {
				icon: 'gps-2',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Personal effects <br /> coverage',
			description:
				'This feature offers protection against the loss of personal belongings up to a limit of ₦20,000',
			icon: {
				icon: 'wallet',
				dir: 'random',
				height: '',
				width: ''
			}
		}
	]
</script>

<template>
	<div>
		<header class="bg-auto-800 px-5 flexed flex-col justify-center md:px-0 sm:pb-24 py-10 sm:pt-24">
			<ui-pad
				:ui="{ paddingBlock: '', paddingInline: 'sm:px-10', width: 'max-w-[1400px]' }"
				class="inline-flex items-center justify-between w-full lg:flex-nowrap flex-wrap gap-y-10"
			>
				<ui-h1
					paragraph="We get it. You've got a million things on your plate. Auto insurance shouldn't be one of them."
					:ui="btnClass"
					tag="Comprehensive Auto"
					:tag-icon="{ dir: 'random', icon: 'bus-white' }"
					primary-btn-text="Protect your vehicle in 5 minutes <span class='max-sm:hidden'>or less</span>"
					secondary-btn-text="Speak to us to get on board"
					:action="{ type: 'onboard', product: ProductCode.COMPREHENSIVE_AUTO }"
				>
					<template #title="{ highlightColor }">
						<span class="text-wrap">
							Focus on your business, we'll protect <span :class="[highlightColor]">your fleet!</span>
						</span>
					</template>
				</ui-h1>
				<animated-comprehensive
					alt="Modern car illustration representing comprehensive auto insurance for fleets"
					src="/v1727102474/comprehensive_kod7jn.webp"
				/>
			</ui-pad>
		</header>

		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />
			<main-benefit-description
				:highlighted-benefits="benefitHighlights"
				title="Fully transparent. Completely flexible."
				class="space-y-5"
				:ui="{ li: { activeClass: 'border-auto-500', activeText: 'text-auto-500' } }"
			>
				<div class="max-w-[712px] w-full text-center mx-auto flexed flex-col">
					<p class="sm:text-xl text-base text-gray-600">
						The perfect coverage tailored to your auto insurance needs
					</p>
				</div>
			</main-benefit-description>
			<ui-spacer />
			<main-productivity-benefits
				title="360° protection for your vehicles on the road."
				description="Your staff drives confidently with our comprehensive auto insurance, ensuring safety for your business and others."
				:team-benefits="teamBenefits"
				product-name="comprehensive cover"
				:ui="{
					container: {
						btn: {
							base: 'bg-auto-500 min-h-[56px] hover:bg-auto-800',
							onHoverPseudo: 'hover:before:bg-auto-800'
						}
					}
				}"
				@trigger="showBenefits = true"
			>
				<template #hover-icon>
					<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
				</template>
			</main-productivity-benefits>
		</ui-pad>
		<ui-spacer />

		<main-auto-premium-calculation title="Calculate your premium" />
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-information-hint category="auto" />
			<ui-spacer />
			<molecules-process-steps
				title="Convenient. Efficient. Fast."
				description="Secure your vehicles in 3 easy steps!"
				:processes="onboardingProcesses"
				:ui="{ strokeText: { strokeColor: '#FFCA99', color: 'text-auto-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-auto-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-auto-800'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct(ProductCode.COMPREHENSIVE_AUTO)"
					>
						<template #icon>
							<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
						</template>
						<span>Get Covered Now</span>
					</ui-btn>
				</template>
			</molecules-process-steps>
		</ui-pad>
		<ui-spacer />
		<molecules-important-guides :guide-list="guideList" />
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>

		<ui-spacer />

		<molecules-demo-card
			description=""
			class="mb-56"
			title="Comprehensive coverage for your business."
			type="auto"
			@secondary-click="viewMoreProducts('auto')"
		/>

		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			:product-name="ProductCode.COMPREHENSIVE_AUTO"
			product-category="auto"
		/>
		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
